/**
 * What a budget row's model list starts as, and the redirects that make a narrow list work.
 *
 * Plain values and functions, out of `EndpointControls.tsx` so that file exports only components —
 * a non-component export there breaks React Fast Refresh.
 */
/**
 * What a budget row allows when nobody has narrowed it: the cheapest tier, and ONLY that.
 *
 * A budget row should default to the cheap model — that is the whole point of a wallet with a $1
 * cap on it. What made this dangerous before was not the narrowness but the missing half: a worker
 * asks for a TIER, `CLAUDE_API_AUTH_SPEC.tier_models` maps sm/md/lg onto three different slugs, and
 * a machine configured for `md` therefore asked for sonnet and was REFUSED by a haiku-only list —
 * with no way for that person to fix it, since the tier lives in their own CLI config. It hid well,
 * too: `test` probes the cheapest ALLOWED model, so the row showed a green tick while real traffic
 * bounced.
 *
 * `aliasesForPinnedModel` is the missing half. Pinning now also REDIRECTS the other tiers onto the
 * pinned slug, so a narrow wallet serves every caller instead of refusing most of them. Cheap and
 * working, rather than cheap and broken.
 */
/** The hub's own initial lists, restated here for the placeholder and the empty-row hint ONLY.
 *
 *  They are no longer written from this screen. `ensure_default_llm_endpoint` (hub) chooses what a
 *  new wallet allows, because it is also what mints a person's default on their first bootstrap --
 *  a path no UI is on, and the reason a person could previously be restricted only if an admin had
 *  happened to open this page. Keep these in step with `MEMBER_DEFAULT_MODELS` / `ORG_DEFAULT_MODELS`
 *  in `hub llm_endpoint.py`; they are a hint, so drift shows as a stale placeholder
 *  rather than as a wrong value on a row. */
export const DEFAULT_MODELS = ['anthropic/claude-haiku-5.5', 'openai/gpt-5-mini', 'openai/text-embedding-3-small'];
export const ORG_DEFAULT_MODELS = ['anthropic/claude-*', 'openai/*'];

/**
 * The one-press allow-lists on a budget row: a whole vendor family each. Pressing several chains them
 * (`withModelEntries`), so an admin can allow Claude AND Gemini without typing either; `Clear` empties the list.
 *
 * Two spellings of one family. The ORG states a ceiling, so it gets the glob (`glob`), which opens the family.
 * A TEAM gets the family's three sizes written out, because a list of exact slugs is what a person reads and
 * edits, and the first slug (small) is where a request for any other model of that family lands
 * (`aliasesForPinnedModel`). The sizes are not kept here: the hub declares them per provider and serves them
 * (`provider-sizes`), and `presetEntries` picks the provider whose three sizes all belong to the family.
 */
export const MODEL_FAMILY_PRESETS = [
  { key: 'anthropic', label: 'Anthropic', glob: 'anthropic/claude-*' },
  { key: 'openai', label: 'OpenAI', glob: 'openai/*' },
  { key: 'gemini', label: 'Gemini', glob: 'google/gemini-*' },
] as const;

/** One provider's declared sizes, as the hub serves them. */
export interface ProviderSizes {
  provider: string;
  sm: string;
  md: string;
  lg: string;
}

function matchesGlob(glob: string, slug: string): boolean {
  const pattern = glob.split('*').map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`^${pattern.join('.*')}$`).test(slug);
}

/**
 * What a family press writes on a row of this level: the glob on the org, and below it the family's three sizes,
 * small to large, taken from the provider the hub declares them for. `null` when no provider declares sizes inside
 * this family (or they have not loaded yet): the button then has nothing honest to write.
 */
export function presetEntries(
  preset: (typeof MODEL_FAMILY_PRESETS)[number],
  scope: BudgetScope,
  providerSizes: readonly ProviderSizes[] | undefined,
): readonly string[] | null {
  if (scope === 'org') return [preset.glob];
  const declared = (providerSizes ?? []).find((row) =>
    [row.sm, row.md, row.lg].every((slug) => matchesGlob(preset.glob, slug)),
  );
  return declared ? [declared.sm, declared.md, declared.lg] : null;
}

/** `models` with each of `entries` added at the end, in order, skipping any already there. */
export function withModelEntries(models: readonly string[], entries: readonly string[]): string[] {
  const out = [...models];
  for (const entry of entries) if (!out.includes(entry)) out.push(entry);
  return out;
}

/** What each level of the hierarchy starts with when its list is empty. */
export type BudgetScope = 'org' | 'team' | 'person';

/**
 * Seeds by level, and the two blanks are the point.
 *
 * A child may only ever NARROW its parent (`filters.is_subset`, enforced on write with a 400), and
 * the check runs against the IMMEDIATE parent chain — not just the root. So every row that states a
 * model becomes a ceiling for everything beneath it. Pinning the team to one model would therefore
 * make "put this one person on sonnet" an illegal write, which is exactly the freedom this page
 * exists to give an admin.
 *
 * Hence: the ORG states the ceiling (what the payer is willing to fund at all), the TEAM states
 * nothing so it can never be the thing that blocks a person, and each PERSON gets the cheap default
 * — visible, and overridable one at a time without touching anyone else.
 */
export const SEED_BY_SCOPE: Record<BudgetScope, string[]> = {
  org: ORG_DEFAULT_MODELS,
  team: DEFAULT_MODELS,
  person: DEFAULT_MODELS,
};

/**
 * The family a slug belongs to, as a glob. `anthropic/claude-haiku-4.5` -> `anthropic/claude-*`.
 *
 * Deliberately a PATTERN and not a list of sibling slugs. An admin setting a budget knows which
 * model they are willing to fund; they do not know which slug each person's CLI is configured to
 * ask for, and enumerating every tier would restate a list that really lives in the worker specs
 * (`CLAUDE_API_AUTH_SPEC.tier_models`) -- a copy that silently stops matching the day a tier is
 * renamed there, taking the redirect with it. One pattern says the intent directly and keeps
 * working for models that did not exist when this was written.
 */
function familyGlobOf(slug: string): string | null {
  const match = /^([^/]+\/[a-z]+)-/.exec(slug);
  return match ? `${match[1]}-*` : null;
}

/**
 * The redirects that make an allowed list STICK, whatever each person's machine asks for.
 *
 * Allowing a model alone only ever produces a refusal: a worker asks for the tier IT is configured
 * for, and if that is not on the list the call dies with "model X not allowed by endpoint Y" --
 * which the person cannot fix, because the tier lives in their own CLI config, not on this page.
 * The hub resolves `filters.aliases` at the ENTRY endpoint BEFORE any filter check and rewrites the
 * request body (`llm_endpoint.py:1340`), so a machine set to sonnet transparently gets what the
 * wallet actually funds.
 *
 * Worked out PER FAMILY, because one list routinely spans several: `anthropic/claude-haiku-4.5` +
 * `openai/gpt-5-mini` is a wallet that serves Claude Code with the first and codex with the second.
 * A redirect never crosses families -- answering a request for one vendor with another's model
 * would be worse than refusing.
 *
 * Two things are deliberately left alone:
 *
 * * **A family already opened by a glob** (`anthropic/claude-*`) needs no redirect -- everything in
 *   it passes on its own -- and adding one would NARROW what the admin opened.
 * * **A model the admin explicitly listed** keeps working as itself. Where a family lists several,
 *   the family glob points at the first and each of the rest is given an identity entry, which wins
 *   because `resolve_alias` prefers an exact key over a glob. So an unlisted tier falls through to
 *   the first, while every listed one is still reachable — a choice the admin made stays a choice.
 */
export function aliasesForPinnedModel(models_allow: string[]): Record<string, string> {
  const byFamily = new Map<string, string[]>();
  for (const slug of models_allow) {
    if (slug.includes('*')) continue; // a glob allows a range; there is nothing to redirect to
    const family = familyGlobOf(slug);
    if (!family) continue;
    byFamily.set(family, [...(byFamily.get(family) ?? []), slug]);
  }

  const out: Record<string, string> = {};
  for (const [family, slugs] of byFamily) {
    if (models_allow.includes(family)) continue; // the family is open by glob already
    out[family] = slugs[0];
    for (const also of slugs.slice(1)) out[also] = also;
    // The same family as the VENDOR writes it, un-prefixed. An OpenRouter slug is
    // `anthropic/claude-haiku-4.5`; a client speaking Anthropic's own API asks for
    // `claude-haiku-4-5`. Both name the model this wallet is pinned to, and a list holding only the
    // routed spelling refuses the native one — a refusal the caller cannot act on, because their SDK
    // chose the name. `*` crosses `/` in the hub's matcher, so a bare key can only ever match a bare
    // request. The Python twin is `aliases_for_pinned` (`hub filters.py`); the two
    // must agree or this screen's repair effect would rewrite what the hub seeded.
    const bare = family.includes('/') ? family.slice(family.indexOf('/') + 1) : null;
    if (bare && !(bare in out) && !models_allow.includes(bare)) out[bare] = slugs[0];
  }
  return out;
}

/** The model family of a slug or of a family glob, vendor-free: `anthropic/claude-*`, `claude-*` -> `claude`;
 *  `google/gemini-3.5-flash` -> `gemini`. */
function familyName(slugOrGlob: string): string {
  const bare = slugOrGlob.includes('/') ? slugOrGlob.slice(slugOrGlob.indexOf('/') + 1) : slugOrGlob;
  return bare.split('-')[0];
}

/** Whether `models_allow` lets `slug` through: listed as is, or covered by one of its globs. */
function isAllowed(slug: string, models_allow: string[]): boolean {
  return models_allow.some((pattern) => {
    if (!pattern.includes('*')) return pattern === slug;
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    return new RegExp(`^${escaped}$`).test(slug);
  });
}

/**
 * Whether a redirect to `slug` can still land: the list allows it, or allows another model of the SAME family
 * (a team that narrowed the family to one model). The hub follows a redirect to the model the list does allow
 * from that family, so a target that is merely not the one pinned is not a dead end.
 */
function reachable(slug: string, models_allow: string[]): boolean {
  return isAllowed(slug, models_allow) || models_allow.some((entry) => familyName(entry) === familyName(slug));
}

/**
 * The alias map a row should carry: what the allowed list derives (`aliasesForPinnedModel`), PLUS the
 * redirects an admin wrote by hand that the derivation can never make -- those that cross families
 * (`anthropic/claude-*` -> `google/gemini-3.5-flash`: Claude Code asking for Claude, answered with
 * the one model the wallet funds) and still point at a model the list allows.
 *
 * Without this the screen's repair effect rewrote the map from the list alone on every open, so a
 * redirect an admin typed and saved was gone the next time anyone looked. Same-family entries stay
 * fully derived: widening a wallet back to a glob must still clear the redirect it used to need.
 */
export function reconcileAliases(current: Record<string, string>, models_allow: string[]): Record<string, string> {
  const derived = aliasesForPinnedModel(models_allow);
  const kept: Record<string, string> = {};
  for (const [from, to] of Object.entries(current)) {
    if (from in derived) continue;
    if (familyName(from) === familyName(to)) continue; // derived territory: rebuilt from the list
    if (!reachable(to, models_allow)) continue; // no model of that family is allowed: a redirect into a refusal
    kept[from] = to;
  }
  return { ...kept, ...derived };
}

/** Same map, whatever the key order. */
export function sameAliases(a: Record<string, string>, b: Record<string, string>): boolean {
  const norm = (m: Record<string, string>) => JSON.stringify(Object.entries(m).sort(([x], [y]) => (x < y ? -1 : 1)));
  return norm(a) === norm(b);
}

/**
 * The cross-family redirects on a row, as `[from, to]` family names (`['claude', 'gemini']`), one per
 * pair. What the "suggested for this provider" note names after a provider's defaults were written.
 */
export function crossFamilyRedirects(aliases: Record<string, string>): Array<[string, string]> {
  const seen = new Set<string>();
  const out: Array<[string, string]> = [];
  for (const [from, to] of Object.entries(aliases)) {
    const pair: [string, string] = [familyName(from), familyName(to)];
    if (pair[0] === pair[1] || seen.has(pair.join('>'))) continue;
    seen.add(pair.join('>'));
    out.push(pair);
  }
  return out;
}
